// notificationService — client for /api/notifications (Phase 46, plan 46-18).
//
// Mirrors merchantService.ts: axios + getApiBaseUrl() + getHeaders(), and every call catches
// into `{ ok: false, error }` so callers never need a try/catch. Backend: routes/notifications.ts
// (authed, 46-12) and routes/notificationTokens.ts (public token routes, 46-10).
//
// The audience param is a hint only: the server enforces isAdmin for audience=admin (T-46-18-03).

import axios from "axios";
import { getApiBaseUrl, getHeaders } from "./apiClient";
import type { BackendApiResponse } from "./adminService";
import type {
  NotificationAudience as SharedNotificationAudience,
  NotificationDeliveryChannel,
  NotificationPayload,
  NotificationType,
  PreferenceRow,
} from "@shared/notifications";

// Base already includes `/api` (see apiClient.getApiBaseUrl), so never prefix `/api` here.
const BASE = "/notifications";

const FALLBACK_ERROR = "مقدرناش نجيب التنبيهات. جرّب تاني كمان شوية.";

// ─── Types ───

export type NotificationAudience = SharedNotificationAudience;

// JSON over the wire: dates arrive as ISO strings.
export type ClientNotification = {
  id: string;
  type: NotificationType;
  audience: NotificationAudience;
  payload: NotificationPayload;
  actorNames: string[];
  actorCount: number;
  deepLink: string;
  unseen: boolean;
  read: boolean;
  lastEventAt: string;
  createdAt: string;
};

export type NotificationListResponse = {
  items: ClientNotification[];
  nextCursor: string | null;
};

export type PreferenceMatrix = Partial<Record<NotificationType, Record<NotificationDeliveryChannel, boolean>>>;

export type ChannelEligibility = {
  email: { state: "verified" | "unverified" | "none"; email?: string };
  whatsapp: { state: "consented" | "available" | "none"; phoneLast3?: string };
  push: { subscribed: boolean };
};

export type WhatsappConsentOptionView =
  | { kind: "login"; phoneLast3: string }
  | { kind: "claim"; claimId: string; phoneLast3: string };

/** What the client sends: an option by reference, never a phone number (T-46-12-03). */
export type WhatsappConsentOption = { kind: "login" } | { kind: "claim"; claimId: string };

// "onboarding": the phone finish screen, welcome card item 3 and the thank-you second ask (Phase 49).
export type WhatsappConsentSource = "settings" | "bell_prompt" | "onboarding";

export type NotificationPreferences = {
  matrix: PreferenceMatrix;
  eligibility: ChannelEligibility;
  rows: PreferenceRow[];
  whatsappOptions: WhatsappConsentOptionView[];
  prompts: {
    whatsappPromptEligible: boolean;
    pushPromptDismissedAt: string | null;
    // Phase 49 (plan 04): the finish-screen decline marker, and whether the bell/inbox WhatsApp
    // card must stay hidden because the ask lives on the welcome card or thank-you (D-23).
    whatsappOnboardingDeclined?: boolean;
    whatsappBellSuppressed?: boolean;
  };
};

export type PushSubscriptionJSONBody = {
  endpoint: string;
  keys: { p256dh: string; auth: string };
};

export type UnsubscribeTokenType = NotificationType | "*";

// ─── Internal ───

const unwrap = <T>(error: unknown): BackendApiResponse<T> => {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    return { ok: false, error: error.response.data.error };
  }
  return { ok: false, error: FALLBACK_ERROR };
};

const url = (path = "") => `${getApiBaseUrl()}${BASE}${path}`;

// apiClient.getHeaders() only reads the `yetfarrag_auth` blob, which Google and phone logins
// write but email/password and Facebook logins do not (AuthContext writes the bare JWT to
// `auth_token`). Every signed-in user must reach their inbox, so prefer `auth_token`.
const authHeaders = (withAuth: boolean): Record<string, string> => {
  const headers = getHeaders(withAuth);
  if (!withAuth || typeof window === "undefined") return headers;
  try {
    const token = window.localStorage.getItem("auth_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch {
    // Storage blocked (private mode): keep whatever getHeaders() found.
  }
  return headers;
};

const get = async <T>(path: string, params?: Record<string, unknown>, withAuth = true): Promise<BackendApiResponse<T>> => {
  try {
    const response = await axios.get(url(path), { headers: authHeaders(withAuth), params });
    return response.data;
  } catch (error) {
    return unwrap<T>(error);
  }
};

const post = async <T>(
  path: string,
  body?: unknown,
  params?: Record<string, unknown>,
  withAuth = true,
): Promise<BackendApiResponse<T>> => {
  try {
    const response = await axios.post(url(path), body ?? {}, { headers: authHeaders(withAuth), params });
    return response.data;
  } catch (error) {
    return unwrap<T>(error);
  }
};

// ─── Inbox ───

export const getNotifications = async ({
  audience,
  cursor,
  limit,
}: {
  audience: NotificationAudience;
  cursor?: string | null;
  limit?: number;
}) =>
  get<NotificationListResponse>("", {
    audience,
    ...(cursor ? { cursor } : {}),
    ...(limit ? { limit } : {}),
  });

export const getUnreadCount = async ({ audience }: { audience: NotificationAudience }) =>
  get<{ count: number }>("/unread-count", { audience });

export const markNotificationsSeen = async ({ audience }: { audience: NotificationAudience }) =>
  post<{ updated: number }>("/seen", { audience }, { audience });

export const markNotificationRead = async (id: string) =>
  post<{ id: string }>(`/${encodeURIComponent(id)}/read`);

export const markAllNotificationsRead = async ({ audience }: { audience: NotificationAudience }) =>
  post<{ updated: number }>("/read-all", { audience }, { audience });

// ─── Preferences ───

export const getNotificationPreferences = async () => get<NotificationPreferences>("/preferences");

export const updateNotificationPreferences = async ({
  types,
  channel,
  enabled,
}: {
  types: NotificationType[];
  channel: NotificationDeliveryChannel;
  enabled: boolean;
}): Promise<BackendApiResponse<{ updated: number }>> => {
  try {
    const response = await axios.patch(url("/preferences"), { types, channel, enabled }, { headers: authHeaders(true) });
    return response.data;
  } catch (error) {
    return unwrap<{ updated: number }>(error);
  }
};

// ─── WhatsApp consent ───

export const grantWhatsappConsent = async ({
  option,
  source,
}: {
  option: WhatsappConsentOption;
  source: WhatsappConsentSource;
}) => {
  // Rebuild from known keys so nothing extra (e.g. a phone) ever leaves the client.
  const safeOption: WhatsappConsentOption =
    option.kind === "claim" ? { kind: "claim", claimId: option.claimId } : { kind: "login" };
  return post<{ phoneLast3: string }>("/whatsapp/consent", { option: safeOption, source });
};

export const revokeWhatsappConsent = async () => post<{ done: boolean }>("/whatsapp/revoke");

export const dismissWhatsappPrompt = async () => post<{ done: boolean }>("/whatsapp/prompt-dismiss");

// ─── Web Push ───

/** Public: works before sign-in. 503 `push_not_configured` when VAPID is unset. */
export const getPushPublicKey = async () => get<{ publicKey: string }>("/push/public-key", undefined, false);

export const dismissPushPrompt = async () => post<{ done: boolean }>("/push/prompt-dismiss");

export const savePushSubscription = async (sub: PushSubscriptionJSONBody) =>
  post<{ endpoint: string }>("/push/subscriptions", {
    endpoint: sub.endpoint,
    keys: { p256dh: sub.keys?.p256dh, auth: sub.keys?.auth },
  });

export const removePushSubscription = async ({ endpoint }: { endpoint: string }) =>
  post<{ removed: boolean }>("/push/subscriptions/remove", { endpoint });

// ─── Token routes (public; the signed token is the authorisation) ───

export const getUnsubscribeInfo = async (token: string) =>
  get<{ type: UnsubscribeTokenType }>("/unsubscribe/info", { token }, false);

/** JSON POST, so the server answers with `{ ok, data }` rather than the RFC 8058 empty 200. */
export const confirmUnsubscribe = async (token: string) =>
  post<{ type: UnsubscribeTokenType }>("/unsubscribe", { token }, undefined, false);

export const sendEmailVerification = async () => post<{ sent: true }>("/email/verify/send");

export const confirmEmailVerification = async (token: string) =>
  post<{ verified: true }>("/email/verify/confirm", { token }, undefined, false);
